import { Component, ElementRef, QueryList, ViewChildren, inject } from '@angular/core';
import type { AGUFileDropRejection } from 'agu_app_shell';
import { OEP_ENTITY, OEP_FILE_CATEGORY, OEP_LIVE_FILTER, OEP_UPLOAD_RULES, type OEPFileKind } from 'agu_oep_core';
import type {
    FeedbackPlatformConversationMessageEntity, FeedbackPlatformConversation__FeedbackPlatformEntity,
    FeedbackPlatformParticipantEntity, FeedbackPlatformReviewThankYouNoteEntity,
} from 'mj_generatedentities';
import { OEPViewBase } from '../../state/oep-view-base';
import { OEPDataService } from '../../data/oep-data.service';
import { OEPFilesService } from '../../data/oep-files.service';
import { OEP_TAB_BLOCKED, OEPOpenInNewTab } from '../shared/open-tab';
import { OEP_NOTE_MAX, type OEPStatusLine } from '../shared/status';
import { OEPPresentationLinkSql, OEPUploadedFilesSql } from '../shared/files';
import {
    BuildPresentationVM, OEP_CAREER_FIRST,
    type FeedbackItemVM, type OEPCareerStage, type OEPPresentationFile, type OEPReviewInput, type PresentationVM,
} from './presentation.vm';
import { DateOrNull, NumOrNull, Str, StrOrNull, Up, type OEPRow } from '../shared/rows';


/** Only items on a submitted slate count (mirrors the presenter RLS filter, tools/metadata/oep-permissions.js). */
export const OEP_SUBMITTED_SLATE = `CommitteeSelectionID IN (SELECT ID FROM FeedbackPlatform.CommitteeSelection WHERE Status='Submitted')`;

/**
 * Presenter view ("Your presentation") — markup.html:301-491. All copy and rules live in
 * presentation.vm.ts (pure, spec'd); this gathers PresentationInputs in ONE RunViews batch
 * (OEPDataService.Batch, every row RLS-scoped to the presenter server-side) and performs the writes.
 * Uploads go through OEPFilesService → OEPFileResolver (request → PUT → confirm); the resolver
 * owns ownership/window/type/size, and every refusal is shown in an aria-live status.
 */
@Component({
    standalone: false,
    selector: 'oep-presentation',
    templateUrl: './oep-presentation.component.html',
    styleUrl: './oep-presentation.component.scss',
})
export class OEPPresentationComponent extends OEPViewBase {
    private readonly files = inject(OEPFilesService);

    VM: PresentationVM | null = null;
    /** True when the signed-in user has no live presentation this season. */
    NoPresentation = false;
    /** The resolver's own rules (agu_oep_core OEP_UPLOAD_RULES), so the drop rejects exactly what the server would. */
    readonly Accept: readonly string[] = OEP_UPLOAD_RULES.Types;
    readonly MaxBytes: number = OEP_UPLOAD_RULES.MaxBytes;
    readonly NoteMax = OEP_NOTE_MAX;

    /** Which kind is uploading, and its progress 0..1 (null when none). */
    Uploading: OEPFileKind | null = null;
    Progress: number | null = null;
    Replacing: Record<OEPFileKind, boolean> = { Draft: false, Final: false };
    UploadStatus: Record<OEPFileKind, OEPStatusLine | null> = { Draft: null, Final: null };
    CareerStatus: OEPStatusLine | null = null;
    @ViewChildren('careerChip') private chips?: QueryList<ElementRef<HTMLButtonElement>>;

    /** The draft panel was clicked while disabled (no career stage yet): say why under the chips, and put focus on them. */
    DraftBlocked(): void {
        this.CareerStatus = { Text: OEP_CAREER_FIRST, Tone: 'error' };
        this.chips?.first?.nativeElement.focus();
        this.Changed();
    }
    /** Per review: textarea drafts and result lines. */
    Message: Record<string, string> = {};
    Thanks: Record<string, string> = {};
    ThreadStatus: Record<string, OEPStatusLine | null> = {};
    ThanksStatus: Record<string, OEPStatusLine | null> = {};

    private presentationID: string | null = null;
    private participantID: string | null = null;
    /** Sizes of files uploaded in this session (MJ: Files stores none). */
    private readonly sizes = new Map<string, number>();
    /** A conversation created by a send whose message then failed, so a retry reuses it (one per review). */
    private readonly createdConversations = new Map<string, string>();

    protected async Load(): Promise<void> {
        const season = this.Context.Season;
        if (!season) return;
        const me = OEPDataService.G(this.Context.Me.UserID);
        const sid = OEPDataService.G(season.ID);
        const complete = season.Phase === 'Complete';
        const myPres = `SELECT ID FROM FeedbackPlatform.Presentation WHERE PresenterUserID='${me}' AND SeasonID='${sid}' AND ${OEP_LIVE_FILTER.Presentation}`;
        const presLink = OEPPresentationLinkSql(myPres);
        const released = `SELECT r.ID FROM FeedbackPlatform.Review r JOIN FeedbackPlatform.ReviewAssignment ra ON ra.ID = r.ReviewAssignmentID WHERE r.Status = 'Completed' AND ra.PresentationID IN (${myPres})`;
        const reviewForm = `FormType='Review' AND SeasonID IS NULL`;
        const [pres, sessions, participants, fileRows, links, assignments, reviews, responses, questions, rubrics, conversations, messages, notes, selections = []] = await this.Data.Batch([
            { EntityName: OEP_ENTITY.Presentations, ExtraFilter: `ID IN (${myPres})`, Fields: ['ID', 'Title', 'Section', 'SessionID', 'Format', 'PresentationCode'], OrderBy: '__mj_CreatedAt, ID' },
            { EntityName: OEP_ENTITY.Sessions, ExtraFilter: `ID IN (SELECT SessionID FROM FeedbackPlatform.Presentation WHERE ID IN (${myPres}))`, Fields: ['ID', 'Code', 'Format'] },
            // Own row + the released reviewers' rows (institution) — RLS (T5) decides which reviewers are visible.
            { EntityName: OEP_ENTITY.Participants, ExtraFilter: `SeasonID='${sid}' AND (UserID='${me}' OR UserID IN (SELECT ReviewerUserID FROM FeedbackPlatform.ReviewAssignment WHERE PresentationID IN (${myPres})))`, Fields: ['ID', 'UserID', 'Institution', 'CareerStage'] },
            { EntityName: OEP_ENTITY.Files, ExtraFilter: OEPUploadedFilesSql(myPres, ['Draft', 'Final']), Fields: ['ID', 'Name', 'Category', 'Status', 'ContentType', '__mj_CreatedAt'], OrderBy: '__mj_CreatedAt DESC' },
            { EntityName: OEP_ENTITY.FileEntityRecordLinks, ExtraFilter: presLink, Fields: ['FileID', 'RecordID'] },
            { EntityName: OEP_ENTITY.ReviewAssignments, ExtraFilter: `PresentationID IN (${myPres})`, Fields: ['ID', 'PresentationID', 'ReviewerUserID', 'ReviewerUser'] },
            { EntityName: OEP_ENTITY.Reviews, ExtraFilter: `ID IN (${released})`, Fields: ['ID', 'ReviewAssignmentID', 'CompletedDate'], OrderBy: 'CompletedDate, ID' },
            { EntityName: OEP_ENTITY.ReviewResponses, ExtraFilter: `ReviewID IN (${released})`, Fields: ['ReviewID', 'ReviewQuestionID', 'NumericValue', 'TextValue', 'RubricLevelID'] },
            { EntityName: OEP_ENTITY.ReviewQuestions, ExtraFilter: reviewForm, Fields: ['ID', 'Sequence'], OrderBy: 'Sequence' },
            { EntityName: OEP_ENTITY.ReviewQuestionRubrics, ExtraFilter: `ReviewQuestionID IN (SELECT ID FROM FeedbackPlatform.ReviewQuestion WHERE ${reviewForm})`, Fields: ['ID', 'ReviewQuestionID', 'RatingValue', 'RatingLabel'] },
            { EntityName: OEP_ENTITY.Conversations, ExtraFilter: `ReviewID IN (${released})`, Fields: ['ID', 'ReviewID'] },
            { EntityName: OEP_ENTITY.ConversationMessages, ExtraFilter: `ThreadPosition IS NOT NULL AND ConversationID IN (SELECT ID FROM FeedbackPlatform.Conversation WHERE ReviewID IN (${released}))`, Fields: ['ConversationID', 'SenderUserID', 'MessageContent', 'ThreadPosition'], OrderBy: 'ThreadPosition' },
            { EntityName: OEP_ENTITY.ReviewThankYouNotes, ExtraFilter: `ReviewID IN (${released})`, Fields: ['ReviewID'] },
            // Visible to the presenter only once the season is Complete and the slate is Submitted (T5 RLS);
            // asked for only then, and the Submitted check is repeated here (defense in depth: a Draft slate never announces a winner).
            ...(complete ? [{ EntityName: OEP_ENTITY.CommitteeSelectionItems, ExtraFilter: `PresentationID IN (${myPres}) AND ${OEP_SUBMITTED_SLATE}`, Fields: ['PresentationID'] }] : []),
        ]);

        const p = pres[0];
        this.NoPresentation = p === undefined;
        if (!p) {
            this.VM = null;
            this.presentationID = null;
            return;
        }
        const pid = Str(p, 'ID');
        this.presentationID = pid;
        const session = sessions.find((s) => Up(Str(s, 'ID')) === Up(Str(p, 'SessionID')));
        const mine = participants.find((x) => Up(Str(x, 'UserID')) === Up(me));
        this.participantID = mine ? Str(mine, 'ID') : null;

        const linked = new Set(links.filter((l) => Up(Str(l, 'RecordID')) === Up(pid)).map((l) => Up(Str(l, 'FileID'))));
        const latest = (category: string): OEPPresentationFile | null => {
            const f = fileRows.find((x) => Str(x, 'Category') === category && linked.has(Up(Str(x, 'ID')))); // rows are newest first
            if (!f) return null;
            const id = Str(f, 'ID');
            return { FileID: id, Name: Str(f, 'Name'), SizeBytes: this.sizes.get(Up(id)) ?? null, UploadedAt: DateOrNull(f, '__mj_CreatedAt') ?? new Date(0) };
        };

        const seqByQuestion = new Map(questions.map((q) => [Up(Str(q, 'ID')), Number(q['Sequence'])]));
        const rubricByID = new Map(rubrics.map((r) => [Up(Str(r, 'ID')), r]));
        const rubricFor = (questionID: string, value: number | null): OEPRow | undefined =>
            value === null ? undefined : rubrics.find((r) => Up(Str(r, 'ReviewQuestionID')) === Up(questionID) && Number(r['RatingValue']) === value);
        const assignmentByID = new Map(assignments.map((a) => [Up(Str(a, 'ID')), a]));
        const institutionOf = (userID: string): string | null => {
            const row = participants.find((x) => Up(Str(x, 'UserID')) === Up(userID));
            return row ? StrOrNull(row, 'Institution') : null;
        };
        const thanked = new Set(notes.map((n) => Up(Str(n, 'ReviewID'))));

        const reviewInputs: OEPReviewInput[] = reviews
            .filter((r) => {
                const a = assignmentByID.get(Up(Str(r, 'ReviewAssignmentID')));
                return a !== undefined && Up(Str(a, 'PresentationID')) === Up(pid);
            })
            .map((r) => {
                const rid = Str(r, 'ID');
                const a = assignmentByID.get(Up(Str(r, 'ReviewAssignmentID')));
                const mineResponses = responses.filter((x) => Up(Str(x, 'ReviewID')) === Up(rid));
                const bySeq = (seq: number): OEPRow | undefined => mineResponses.find((x) => seqByQuestion.get(Up(Str(x, 'ReviewQuestionID'))) === seq);
                const conv = conversations.find((c) => Up(Str(c, 'ReviewID')) === Up(rid));
                const convID = conv ? Str(conv, 'ID') : (this.createdConversations.get(Up(rid)) ?? null);
                return {
                    ReviewID: rid,
                    ReviewerName: a ? Str(a, 'ReviewerUser') : '',
                    Institution: a ? institutionOf(Str(a, 'ReviewerUserID')) : null,
                    ReleasedOn: DateOrNull(r, 'CompletedDate'),
                    Scores: mineResponses.flatMap((x) => {
                        const seq = seqByQuestion.get(Up(Str(x, 'ReviewQuestionID')));
                        if (seq === undefined || seq < 1 || seq > 5) return [];
                        const level = StrOrNull(x, 'RubricLevelID');
                        const rubric = (level ? rubricByID.get(Up(level)) : undefined) ?? rubricFor(Str(x, 'ReviewQuestionID'), NumOrNull(x, 'NumericValue'));
                        return [{
                            Sequence: seq,
                            Level: rubric ? Number(rubric['RatingValue']) : null,
                            LevelLabel: rubric ? Str(rubric, 'RatingLabel') : '',
                            Text: StrOrNull(x, 'TextValue') ?? '',
                        }];
                    }),
                    Strengths: StrOrNull(bySeq(6) ?? {}, 'TextValue') ?? '',
                    Suggestions: StrOrNull(bySeq(7) ?? {}, 'TextValue') ?? '',
                    ConversationID: convID,
                    Thread: convID === null ? [] : messages
                        .filter((m) => Up(Str(m, 'ConversationID')) === Up(convID))
                        .map((m) => ({ SenderUserID: Str(m, 'SenderUserID'), Body: Str(m, 'MessageContent') })),
                    ThankYouSent: thanked.has(Up(rid)),
                };
            });

        this.VM = BuildPresentationVM({
            Phase: season.Phase, Timeline: season.Timeline, SeasonCode: season.Code, Me: me,
            Presentation: {
                ID: pid, Title: Str(p, 'Title'), SectionName: Str(p, 'Section'),
                Format: StrOrNull(p, 'Format') ?? (session ? StrOrNull(session, 'Format') : null),
                Code: StrOrNull(p, 'PresentationCode') ?? (session ? StrOrNull(session, 'Code') : null),
            },
            CareerStage: mine ? StrOrNull(mine, 'CareerStage') : null,
            Draft: latest(OEP_FILE_CATEGORY.Draft),
            Final: latest(OEP_FILE_CATEGORY.Final),
            Reviews: reviewInputs,
            IsWinner: complete ? selections.some((s) => Up(Str(s, 'PresentationID')) === Up(pid)) : null,
            Resources: this.Context.Resources.presenter ?? [],
        });
    }

    async PickCareer(stage: OEPCareerStage): Promise<void> {
        const participantID = this.participantID;
        this.CareerStatus = null;
        if (participantID === null) {
            this.CareerStatus = { Text: 'Your OEP participant record is missing. Write to membership@agu.org.', Tone: 'error' };
            this.Changed();
            return;
        }
        const ok = await this.Act(async () => {
            await this.Data.Update<FeedbackPlatformParticipantEntity>(OEP_ENTITY.Participants, participantID, (e) => { e.CareerStage = stage; });
        }, null, (m) => { this.CareerStatus = { Text: m, Tone: 'error' }; });
        if (ok) this.CareerStatus = { Text: `Career stage saved: ${stage}.`, Tone: 'ok' };
        this.Changed();
    }

    async UploadFile(kind: OEPFileKind, file: File): Promise<void> {
        const presentationID = this.presentationID;
        if (presentationID === null || this.Busy) return;
        this.UploadStatus[kind] = null;
        this.Uploading = kind;
        this.Progress = 0;
        this.Changed();
        const ok = await this.Act(async () => {
            const r = await this.files.Upload(presentationID, kind, file, (fraction) => { this.Progress = fraction; this.Changed(); });
            this.sizes.set(Up(r.FileID), file.size);
        }, null, (m) => { this.UploadStatus[kind] = { Text: m, Tone: 'error' }; });
        this.Uploading = null;
        this.Progress = null;
        if (ok) {
            this.Replacing[kind] = false;
            this.UploadStatus[kind] = { Text: `Uploaded ${file.name}.`, Tone: 'ok' };
        }
        this.Changed();
    }

    Rejected(kind: OEPFileKind, r: AGUFileDropRejection): void {
        this.UploadStatus[kind] = { Text: `${r.FileName}: ${r.Reason === 'type' ? OEP_UPLOAD_RULES.Messages.Type : OEP_UPLOAD_RULES.Messages.Size}`, Tone: 'error' };
        this.Changed();
    }

    StartReplace(kind: OEPFileKind): void {
        this.Replacing[kind] = true;
        this.UploadStatus[kind] = null;
        this.Changed();
    }

    CancelReplace(kind: OEPFileKind): void {
        this.Replacing[kind] = false;
        this.Changed();
    }

    async OpenFile(kind: OEPFileKind, fileID: string): Promise<void> {
        try {
            const tab = await OEPOpenInNewTab(() => this.files.DownloadUrl(fileID));
            if (tab.Blocked) this.UploadStatus[kind] = { Text: OEP_TAB_BLOCKED, Tone: 'error', Link: { Label: 'Open the file', Url: tab.Url } };
        } catch (err) {
            this.UploadStatus[kind] = { Text: err instanceof Error ? err.message : String(err), Tone: 'error' };
        }
        this.Changed();
    }

    /** Trimmed text when it is sendable (1..NoteMax characters), else null. */
    private sendable(text: string | undefined): string | null {
        const t = (text ?? '').trim();
        return t.length > 0 && t.length <= OEP_NOTE_MAX ? t : null;
    }

    CanSend(item: FeedbackItemVM): boolean {
        return item.CanWrite && !this.Busy && this.sendable(this.Message[item.ReviewID]) !== null;
    }

    CanSendThanks(item: FeedbackItemVM): boolean {
        return item.CanThank && !this.Busy && this.sendable(this.Thanks[item.ReviewID]) !== null;
    }

    async Send(item: FeedbackItemVM): Promise<void> {
        const body = this.sendable(this.Message[item.ReviewID]);
        if (body === null || !item.CanWrite) return;
        const me = this.Context.Me.UserID;
        const reviewKey = Up(item.ReviewID);
        this.ThreadStatus[item.ReviewID] = null;
        const ok = await this.Act(async () => {
            let conversationID = item.ConversationID ?? this.createdConversations.get(reviewKey) ?? null;
            if (conversationID === null) {
                const c = await this.Data.SaveNew<FeedbackPlatformConversation__FeedbackPlatformEntity>(OEP_ENTITY.Conversations, (e) => {
                    e.ReviewID = item.ReviewID; e.Subject = 'Feedback conversation'; e.Status = 'Active'; e.StartedDate = new Date();
                });
                conversationID = c.ID;
                this.createdConversations.set(reviewKey, c.ID);
            }
            const cid = conversationID;
            await this.Data.SaveNew<FeedbackPlatformConversationMessageEntity>(OEP_ENTITY.ConversationMessages, (m) => {
                m.ConversationID = cid; m.SenderUserID = me; m.MessageContent = body; m.SentDate = new Date(); m.Status = 'Sent'; m.MessageType = 'Message';
            });
        }, null, (m) => { this.ThreadStatus[item.ReviewID] = { Text: m, Tone: 'error' }; });
        if (ok) {
            this.Message[item.ReviewID] = '';
            this.ThreadStatus[item.ReviewID] = { Text: 'Message sent.', Tone: 'ok' };
        }
        this.Changed();
    }

    async SendThanks(item: FeedbackItemVM): Promise<void> {
        const note = this.sendable(this.Thanks[item.ReviewID]);
        if (note === null || !item.CanThank) return;
        this.ThanksStatus[item.ReviewID] = null;
        const ok = await this.Act(async () => {
            await this.Data.SaveNew<FeedbackPlatformReviewThankYouNoteEntity>(OEP_ENTITY.ReviewThankYouNotes, (n) => { n.ReviewID = item.ReviewID; n.Note = note; });
        }, null, (m) => { this.ThanksStatus[item.ReviewID] = { Text: m, Tone: 'error' }; });
        if (ok) {
            this.Thanks[item.ReviewID] = '';
            this.ThanksStatus[item.ReviewID] = { Text: 'Thank you note sent.', Tone: 'ok' };
        }
        this.Changed();
    }

    ProgressLabel(): string {
        return this.Progress === null ? '' : `Uploading… ${Math.round(this.Progress * 100)}%`;
    }
}
